/**
 * Interactive drawing-schema factories — Phase 6a-5 commit 15 of the
 * decomposition (briefs/ARCHITECTURE_DECOMPOSITION_BLUEPRINT.md §6, A16f,
 * v2.13/R29). Schema literals moved VERBATIM from static/index.html's add*
 * constructors — INCLUDING KEY ORDER, because JSON.stringify serializes in
 * insertion order and saveDrawings() plain-stringifies these objects to
 * localStorage and /api/drawings: key order IS the persisted format.
 *
 * OWNERSHIP MODEL (v2.13 ruling):
 *  - The INTERACTIVE schemas below (the original ten, plus rr_position) are canonical HERE. Persisted-field
 *    changes to them happen only in this file.
 *  - Four PERMANENT page-owned schema VARIANTS exist by design and are NOT
 *    duplicates: auto-trendline + auto-fib (extra `source`/`p3` fields,
 *    opposite extend defaults, style-driven colours, batch orchestration),
 *    hline (addHorizontalLine), avwap (addAnchoredVwap). Variant schemas
 *    change only in their page-side constructors. Unifying a variant into
 *    these factories requires a blueprint amendment proving byte-for-byte
 *    persistence compatibility.
 *
 * Preserved historical quirks (load-bearing persisted format):
 *  - trendline/rect/fib use `point1`/`point2`; the other seven use
 *    `p1`/`p2`(/`p3`).
 *  - parallel_channel/text/price_range CLONE their points; the rest store
 *    the caller's objects BY REFERENCE.
 *
 * The page's add* wrappers own everything else: id allocation
 * (`state.nextDrawingId++` — passed in as `id`), the bridged `state.symbol`
 * read (passed in as `symbol`), push/save/redraw/history/badge, and the
 * per-function return-value split. Zero callbacks — id and symbol are
 * parameters (rule 6).
 */

import { POSITION_COLORS, POSITION_TYPE, type PositionSide } from "./position_tool";

export interface DrawingPoint { time: number; price: number; }

export function makeTrendline(id: string, symbol: string, p1: DrawingPoint, p2: DrawingPoint) {
  return {
    id, type: 'trendline',
    point1: p1, point2: p2,
    color: '#787B86', lineWidth: 2, lineStyle: 0, opacity: 1,
    title: '', locked: false, symbol, hidden: false,
  };
}

export function makeRectangle(id: string, symbol: string, p1: DrawingPoint, p2: DrawingPoint) {
  return {
    id, type: 'rect',
    point1: p1, point2: p2,
    color: '#787B86', lineWidth: 2, lineStyle: 0, opacity: 1,
    title: '', locked: false, symbol, hidden: false,
  };
}

export function makeHorizontalLine(id: string, symbol: string, price: number) {
  return {
    id,
    type: 'hline',
    price,
    color: '#787B86',
    lineWidth: 2,
    lineStyle: 0,
    opacity: 1,
    title: '',
    locked: false,
    symbol,
  };
}

export function makeFibonacci(id: string, symbol: string, p1: DrawingPoint, p2: DrawingPoint) {
  return {
    id, type: 'fib',
    point1: p1, point2: p2,
    color: '#787B86', lineWidth: 2, opacity: 1, extend: false, extendMode: 'off',
    title: '', locked: false, symbol, hidden: false,
  };
}

export function makeExtendedLine(id: string, symbol: string, p1: DrawingPoint, p2: DrawingPoint) {
  return {
    id, type: 'extended_line',
    p1, p2,
    color: '#787B86', lineWidth: 2, lineStyle: 0, opacity: 1,
    title: '', locked: false, symbol, hidden: false,
  };
}

export function makeRay(id: string, symbol: string, p1: DrawingPoint, p2: DrawingPoint) {
  return {
    id, type: 'ray',
    p1, p2,
    color: '#787B86', lineWidth: 2, lineStyle: 0, opacity: 1,
    title: '', locked: false, symbol, hidden: false,
  };
}

export function makeHorizontalRay(id: string, symbol: string, p1: DrawingPoint, p2: DrawingPoint) {
  return {
    id, type: 'hray',
    p1, p2,
    color: '#787B86', lineWidth: 2, lineStyle: 0, opacity: 1,
    title: '', locked: false, symbol, hidden: false,
  };
}

export function makeArrow(id: string, symbol: string, p1: DrawingPoint, p2: DrawingPoint) {
  return {
    id, type: 'arrow',
    p1, p2,
    color: '#787B86', lineWidth: 2, lineStyle: 0, opacity: 1,
    title: '', locked: false, symbol, hidden: false,
  };
}

export function makeParallelChannel(id: string, symbol: string, p1: DrawingPoint, p2: DrawingPoint, p3: DrawingPoint) {
  return {
    id, type: 'parallel_channel',
    p1: { time: p1.time, price: p1.price },
    p2: { time: p2.time, price: p2.price },
    p3: { time: p3.time, price: p3.price },
    color: '#787B86', fillColor: '#787B86', lineWidth: 2, lineStyle: 0, opacity: 1,
    title: '', locked: false, symbol, hidden: false,
  };
}

export function makeText(id: string, symbol: string, p1: DrawingPoint, text: string) {
  return {
    id, type: 'text',
    p1: { time: p1.time, price: p1.price },
    text: text || 'Text',
    fontSize: 14, color: '#E7EBF0', bgColor: '#171A1E',
    title: '', locked: false, symbol, hidden: false,
  };
}

export function makePriceRange(id: string, symbol: string, p1: DrawingPoint, p2: DrawingPoint) {
  return {
    id, type: 'price_range',
    p1: { time: p1.time, price: p1.price },
    p2: { time: p2.time, price: p2.price },
    color: '#787B86', lineWidth: 2, lineStyle: 0, opacity: 1,
    title: '', locked: false, symbol, hidden: false,
  };
}

/**
 * Long/Short Position (risk-reward) planning drawing — an ELEVENTH interactive
 * schema, added after the ten above (briefs/LONG_SHORT_POSITION_TOOL_BRIEF.md).
 * p1 = entry, p2 = stop, p3 = target; the target is passed as a PRICE and given
 * p2's time so p2.time === p3.time holds by construction. Points are cloned.
 * NOT a broker position — see position_tool.ts.
 */
export function makeRRPosition(
  id: string,
  symbol: string,
  side: PositionSide,
  entry: DrawingPoint,
  stop: DrawingPoint,
  targetPrice: number,
) {
  return {
    id, type: POSITION_TYPE, side,
    p1: { time: entry.time, price: entry.price },
    p2: { time: stop.time, price: stop.price },
    p3: { time: stop.time, price: targetPrice },
    color: POSITION_COLORS.entry, profitColor: POSITION_COLORS.profit, lossColor: POSITION_COLORS.loss,
    lineWidth: 2, lineStyle: 0, opacity: 1,
    title: '', locked: false, symbol, hidden: false,
  };
}

/**
 * Removes a drawing by ID from an array of drawings, returning the new array and the removed item.
 */
export function removeDrawingFromList<T extends { id: string }>(
  drawings: T[] | null | undefined,
  id: string,
): { updatedDrawings: T[]; removedDrawing: T | null; removedIndex: number } {
  if (!Array.isArray(drawings) || !id) {
    return {
      updatedDrawings: Array.isArray(drawings) ? [...drawings] : [],
      removedDrawing: null,
      removedIndex: -1,
    };
  }
  const idx = drawings.findIndex((d) => d && d.id === id);
  if (idx < 0) {
    return {
      updatedDrawings: [...drawings],
      removedDrawing: null,
      removedIndex: -1,
    };
  }
  const updatedDrawings = drawings.filter((_, i) => i !== idx);
  return { updatedDrawings, removedDrawing: drawings[idx], removedIndex: idx };
}

